import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { X } from 'lucide-react';
import { municipalities } from '../../data/municipalities';

export function AddFarmerForm({ onClose, onAddFarmer, editData }) {
  const isEditMode = !!editData;

  const [formData, setFormData] = useState({
    name: editData ? editData.name || '' : '',
    age: editData ? (editData.age || '') : '',
    gender: editData ? (editData.gender || '') : '',
    middleInitial: editData ? (editData.middleInitial || '') : '',
    respondent: editData ? (editData.respondent || '') : '',
    civilStatus: editData ? (editData.civilStatus || '') : '',
    education: editData ? (editData.educationalAttainment || editData.education || '') : '',
    household: editData ? (editData.noOfHouseholdMembers || editData.household || '') : '',
    occupation: editData ? (editData.occupation || '') : '',
    sitioPurok: editData ? (editData.sitioPurok || '') : '',
    municipality: editData ? (editData.municipality || '') : '',
    barangay: editData ? (editData.barangay || '') : '',
    landOwnership: editData ? (editData.landOwnership || '') : '',
    landSize: editData ? (editData.farmAreaHa || editData.farmSize || '') : '',
    productionCapacity: editData ? (editData.weightOfBeansPerHarvestKg || editData.productionCapacity || '') : ''
  });

  // Additional nested data fields expected by backend
  const [incomeData, setIncomeData] = useState({
    annualFarmIncome: editData ? (editData.annualFarmIncomeFromCacao || '') : '',
    formOfIncome: editData ? (editData.formOfIncome || '') : '',
    inPesoIncome: editData ? (editData.inPesoIncome || '') : '',
    inKindIncome: editData ? (editData.inKindIncome || '') : ''
  });

  const [membershipData, setMembershipData] = useState({
    affiliationName: editData ? (editData.affiliationNameOfOrganizationCooperative || '') : '',
    yearsInMembership: editData ? (editData.yearsInMembership || '') : '',
    active: editData ? (editData.active === 'Yes' || editData.active === true) : false,
    position: editData ? (editData.position || '') : '',
    designation: editData ? (editData.designation || '') : ''
  });

  const [farmData, setFarmData] = useState({
    yearEstablished: editData ? (editData.yearEstablished || '') : '',
    actualTrees: editData ? (editData.actualNumberOfTreesPlanted || '') : '',
    productiveTreesBefore: editData ? (editData.noOfProductiveTreesBeforeTyphoon || '') : '',
    productiveTreesAfter: editData ? (editData.noOfProductiveTreesAfterTyphoon || '') : '',
    sourceProvider: editData ? (editData.sourceProvider || '') : '',
    propagationTechnique: editData ? (editData.propagationTechnique || '') : '',
    clones: editData ? (editData.clones || '') : '',
    availabilityOfNursery: editData ? (editData.availabilityOfNursery || '') : ''
  });

  const [productivityData, setProductivityData] = useState({
    productivityPodsTreeYear: editData ? (editData.productivityPodsTreeYear || '') : '',
    podsPerTreeHarvest: editData ? (editData.podsPerTreeHarvest || '') : '',
    weightOfBeansPerHarvestKg: editData ? (editData.weightOfBeansPerHarvestKg || '') : '',
    weightBeansHectare: editData ? (editData.weightOfBeansPerHectareKg || '') : ''
  });

  const [knowledgeData, setKnowledgeData] = useState({
    knowledgeOnCacaoFarming: editData ? (editData.knowledgeOnCacaoFarmingTrainingsSeminarAttended || '') : '',
    production: editData ? (editData.production || '') : ''
  });

  const [cacaoVarieties, setCacaoVarieties] = useState(editData ? (editData.cacaoVarieties || []) : []);
  const [trainingReceived, setTrainingReceived] = useState(
    editData && editData.knowledgeOnCacaoFarmingTrainingsSeminarAttended
      ? editData.knowledgeOnCacaoFarmingTrainingsSeminarAttended.split(',').map(t => t.trim()).filter(Boolean)
      : []
  );
  const [newVariety, setNewVariety] = useState('');
  const [newTraining, setNewTraining] = useState('');

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleIncomeChange = (field, value) => setIncomeData(prev => ({ ...prev, [field]: value }));
  const handleMembershipChange = (field, value) => setMembershipData(prev => ({ ...prev, [field]: value }));
  const handleFarmChange = (field, value) => setFarmData(prev => ({ ...prev, [field]: value }));
  const handleProductivityChange = (field, value) => setProductivityData(prev => ({ ...prev, [field]: value }));
  const handleKnowledgeChange = (field, value) => setKnowledgeData(prev => ({ ...prev, [field]: value }));

  const addVariety = () => {
    if (newVariety.trim() && !cacaoVarieties.includes(newVariety.trim())) {
      setCacaoVarieties(prev => [...prev, newVariety.trim()]);
      setNewVariety('');
    }
  };

  const removeVariety = (variety) => {
    setCacaoVarieties(prev => prev.filter(v => v !== variety));
  };

  const addTraining = () => {
    if (newTraining.trim() && !trainingReceived.includes(newTraining.trim())) {
      setTrainingReceived(prev => [...prev, newTraining.trim()]);
      setNewTraining('');
    }
  };

  const removeTraining = (training) => {
    setTrainingReceived(prev => prev.filter(t => t !== training));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Build payload matching backend /insert expected structure
    // Split full name into firstname and lastname (simple heuristic)
    const names = (formData.name || '').trim().split(' ');
    const firstname = names.slice(0, -1).join(' ') || names[0] || '';
    const lastname = names.length > 1 ? names[names.length - 1] : '';

    // Build nested payload objects only when the user provided values.
    const buildIncome = () => {
      const obj = {
        annualFarmIncome: null,
        formOfIncome: null,
        inPesoIncome: null,
        inKindIncome: null
      };
      if (incomeData.annualFarmIncome !== '' && incomeData.annualFarmIncome !== undefined) obj.annualFarmIncome = parseFloat(incomeData.annualFarmIncome);
      if (incomeData.formOfIncome) obj.formOfIncome = incomeData.formOfIncome;
      if (incomeData.inPesoIncome !== '' && incomeData.inPesoIncome !== undefined) obj.inPesoIncome = parseFloat(incomeData.inPesoIncome);
      if (incomeData.inKindIncome) obj.inKindIncome = incomeData.inKindIncome;
      // Only return the object if at least one value was provided
      const hasData = obj.annualFarmIncome !== null || obj.formOfIncome !== null || obj.inPesoIncome !== null || obj.inKindIncome !== null;
      return hasData ? obj : undefined;
    };

    const buildMembership = () => {
      const obj = {
        affiliationName: null,
        yearsInMembership: null,
        active: null,
        position: null,
        designation: null
      };
      if (membershipData.affiliationName) obj.affiliationName = membershipData.affiliationName;
      if (membershipData.yearsInMembership !== '' && membershipData.yearsInMembership !== undefined) obj.yearsInMembership = parseInt(membershipData.yearsInMembership);
      // Convert boolean checkbox to "Yes"/"No" string
      obj.active = membershipData.active ? "Yes" : "No";
      if (membershipData.position) obj.position = membershipData.position;
      if (membershipData.designation) obj.designation = membershipData.designation;
      const hasData = obj.affiliationName !== null || obj.yearsInMembership !== null || obj.active !== null || obj.position !== null || obj.designation !== null;
      return hasData ? obj : undefined;
    };

    const buildFarm = () => {
      const obj = {
        farmArea: null,
        yearEstablished: null,
        actualTrees: null,
        productiveTreesBefore: null,
        productiveTreesAfter: null,
        sourceProvider: null,
        propagationTechnique: null,
        landOwnership: null,
        varietyPlanted: null,
        clones: null,
        availabilityOfNursery: null
      };
      if (formData.landSize !== '' && formData.landSize !== undefined) obj.farmArea = parseFloat(formData.landSize);
      if (farmData.yearEstablished !== '' && farmData.yearEstablished !== undefined) obj.yearEstablished = parseInt(farmData.yearEstablished);
      if (farmData.actualTrees !== '' && farmData.actualTrees !== undefined) obj.actualTrees = parseInt(farmData.actualTrees);
      if (farmData.productiveTreesBefore !== '' && farmData.productiveTreesBefore !== undefined) obj.productiveTreesBefore = parseInt(farmData.productiveTreesBefore);
      if (farmData.productiveTreesAfter !== '' && farmData.productiveTreesAfter !== undefined) obj.productiveTreesAfter = parseInt(farmData.productiveTreesAfter);
      if (farmData.sourceProvider) obj.sourceProvider = farmData.sourceProvider;
      if (farmData.propagationTechnique) obj.propagationTechnique = farmData.propagationTechnique;
      if (formData.landOwnership) obj.landOwnership = formData.landOwnership;
      // Use cacaoVarieties array for multiple varieties
      if (cacaoVarieties.length > 0) obj.varietyPlanted = cacaoVarieties.join(', ');
      if (farmData.clones) obj.clones = farmData.clones;
      if (farmData.availabilityOfNursery) obj.availabilityOfNursery = farmData.availabilityOfNursery;
      const hasData = obj.farmArea !== null || obj.yearEstablished !== null || obj.actualTrees !== null || 
                      obj.productiveTreesBefore !== null || obj.productiveTreesAfter !== null || 
                      obj.sourceProvider !== null || obj.propagationTechnique !== null || 
                      obj.landOwnership !== null || obj.varietyPlanted !== null || 
                      obj.clones !== null || obj.availabilityOfNursery !== null;
      return hasData ? obj : undefined;
    };

    const buildProductivity = () => {
      const obj = {
        productivityPodsTreeYear: null,
        podsPerTreeHarvest: null,
        weightOfBeansPerHarvestKg: null,
        weightOfBeansPerHectareKg: null
      };
      if (productivityData.productivityPodsTreeYear !== '' && productivityData.productivityPodsTreeYear !== undefined) obj.productivityPodsTreeYear = parseFloat(productivityData.productivityPodsTreeYear);
      if (productivityData.podsPerTreeHarvest !== '' && productivityData.podsPerTreeHarvest !== undefined) obj.podsPerTreeHarvest = parseFloat(productivityData.podsPerTreeHarvest);
      if (productivityData.weightOfBeansPerHarvestKg !== '' && productivityData.weightOfBeansPerHarvestKg !== undefined) obj.weightOfBeansPerHarvestKg = parseFloat(productivityData.weightOfBeansPerHarvestKg);
      if (productivityData.weightBeansHectare !== '' && productivityData.weightBeansHectare !== undefined) obj.weightOfBeansPerHectareKg = parseFloat(productivityData.weightBeansHectare);
      else if (formData.productionCapacity !== '' && formData.productionCapacity !== undefined) obj.weightOfBeansPerHectareKg = parseFloat(formData.productionCapacity);
      const hasData = obj.productivityPodsTreeYear !== null || obj.podsPerTreeHarvest !== null || 
                      obj.weightOfBeansPerHarvestKg !== null || obj.weightOfBeansPerHectareKg !== null;
      return hasData ? obj : undefined;
    };

    const buildKnowledge = () => {
      const obj = {
        knowledgeOnCacaoFarming: null,
        production: null
      };
      if (knowledgeData.knowledgeOnCacaoFarming) obj.knowledgeOnCacaoFarming = knowledgeData.knowledgeOnCacaoFarming;
      else if (trainingReceived.length > 0) obj.knowledgeOnCacaoFarming = trainingReceived.join(', ');
      if (knowledgeData.production) obj.production = knowledgeData.production;
      const hasData = obj.knowledgeOnCacaoFarming !== null || obj.production !== null;
      return hasData ? obj : undefined;
    };

    const incomePayload = buildIncome();
    const membershipPayload = buildMembership();
    const farmPayload = buildFarm();
    const productivityPayload = buildProductivity();
    const knowledgePayload = buildKnowledge();

    const payload = {
      // Main farmer data (top-level values for the SP)
      lastname: lastname || null,
      firstname: firstname || (formData.name ? formData.name : null),
      middleInitial: formData.middleInitial || null,
      respondent: formData.respondent || null,
      sitioPurok: formData.sitioPurok || null,
      barangay: formData.barangay || null,
      municipality: formData.municipality || null,
      age: formData.age !== '' ? parseInt(formData.age) : null,
      gender: formData.gender || null,
      civilStatus: formData.civilStatus || null,
      education: formData.education || null,
      household: formData.household !== '' ? parseInt(formData.household) : null,
      occupation: formData.occupation || null,
      // Only spread nested objects if they contain user-provided values
      ...(incomePayload ? { incomeData: incomePayload } : {}),
      ...(membershipPayload ? { membershipData: membershipPayload } : {}),
      ...(farmPayload ? { farmData: farmPayload } : {}),
      ...(productivityPayload ? { productivityData: productivityPayload } : {}),
      ...(knowledgePayload ? { knowledgeData: knowledgePayload } : {})
    };

    // Debug: show exact payload being sent to the API
    // Pass nested payload to parent handler which calls the API
    if (isEditMode) {
      onAddFarmer(payload, editData.id);
    } else {
      onAddFarmer(payload);
    }
    onClose();
  };

  // Minimal validation: require name, age, gender, municipality, barangay
  const isFormValid = () => {
    return formData.name && formData.age && formData.gender && formData.municipality && formData.barangay;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <Card className="border-0 shadow-none">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-amber-900">{isEditMode ? 'Edit Farmer' : 'Add New Farmer'}</CardTitle>
              <Button variant="ghost" onClick={onClose} className="text-gray-500 hover:text-gray-700">
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Personal Information */}
              <div>
                <h3 className="mb-4 text-green-800">Personal Information</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      placeholder="Enter farmer's full name"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="age">Age *</Label>
                    <Input
                      id="age"
                      type="number"
                      value={formData.age}
                      onChange={(e) => handleInputChange('age', e.target.value)}
                      placeholder="Enter age"
                      min="18"
                      max="100"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="gender">Gender *</Label>
                    <Select value={formData.gender} onValueChange={(value) => handleInputChange('gender', value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select gender" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Male">Male</SelectItem>
                        <SelectItem value="Female">Female</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="middleInitial">Middle Initial</Label>
                    <Input
                      id="middleInitial"
                      value={formData.middleInitial}
                      onChange={(e) => handleInputChange('middleInitial', e.target.value)}
                      placeholder="M.I."
                      maxLength="3"
                    />
                  </div>
                  <div>
                    <Label htmlFor="respondent">Respondent</Label>
                    <Input
                      id="respondent"
                      value={formData.respondent}
                      onChange={(e) => handleInputChange('respondent', e.target.value)}
                      placeholder="e.g., Owner, Manager, Caretaker"
                    />
                  </div>
                  <div>
                    <Label htmlFor="civilStatus">Civil Status</Label>
                    <Select value={formData.civilStatus} onValueChange={(value) => handleInputChange('civilStatus', value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select civil status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Single">Single</SelectItem>
                        <SelectItem value="Married">Married</SelectItem>
                        <SelectItem value="Widowed">Widowed</SelectItem>
                        <SelectItem value="Separated">Separated</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="education">Educational Attainment</Label>
                    <Select value={formData.education} onValueChange={(value) => handleInputChange('education', value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select education" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Elementary">Elementary</SelectItem>
                        <SelectItem value="Elementary Graduate">Elementary Graduate</SelectItem>
                        <SelectItem value="High School">High School</SelectItem>
                        <SelectItem value="High School Graduate">High School Graduate</SelectItem>
                        <SelectItem value="College Undergraduate">College Undergraduate</SelectItem>
                        <SelectItem value="College Graduate">College Graduate</SelectItem>
                        <SelectItem value="Vocational">Vocational</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="household">No. Household Members</Label>
                    <Input
                      id="household"
                      type="number"
                      value={formData.household}
                      onChange={(e) => handleInputChange('household', e.target.value)}
                      placeholder="e.g., 4"
                      min="1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="occupation">Occupation</Label>
                    <Input
                      id="occupation"
                      value={formData.occupation}
                      onChange={(e) => handleInputChange('occupation', e.target.value)}
                      placeholder="e.g., Cacao Farmer"
                    />
                  </div>
                </div>

                  {/* Income Information */}
                  <div>
                    <h3 className="mb-4 text-green-800">Income Information (optional)</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="annualFarmIncome">Annual Farm Income (PHP)</Label>
                        <Input
                          id="annualFarmIncome"
                          type="number"
                          step="0.01"
                          value={incomeData.annualFarmIncome}
                          onChange={(e) => handleIncomeChange('annualFarmIncome', e.target.value)}
                          placeholder="e.g., 120000"
                        />
                      </div>
                      <div>
                        <Label htmlFor="formOfIncome">Form of Income</Label>
                        <Input
                          id="formOfIncome"
                          value={incomeData.formOfIncome}
                          onChange={(e) => handleIncomeChange('formOfIncome', e.target.value)}
                          placeholder="e.g., Cash, In-kind"
                        />
                      </div>
                      <div>
                        <Label htmlFor="inPesoIncome">In Peso Income (PHP)</Label>
                        <Input
                          id="inPesoIncome"
                          type="number"
                          step="0.01"
                          value={incomeData.inPesoIncome}
                          onChange={(e) => handleIncomeChange('inPesoIncome', e.target.value)}
                          placeholder="e.g., 80000"
                        />
                      </div>
                      <div>
                        <Label htmlFor="inKindIncome">In Kind Income</Label>
                        <Input
                          id="inKindIncome"
                          value={incomeData.inKindIncome}
                          onChange={(e) => handleIncomeChange('inKindIncome', e.target.value)}
                          placeholder="e.g., cacao seedlings"
                        />
                      </div>
                    </div>
                  </div>
              </div>

              {/* Location Information */}
              <div>
                <h3 className="mb-4 text-green-800">Location Information</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="sitioPurok">Sitio / Purok</Label>
                    <Input
                      id="sitioPurok"
                      value={formData.sitioPurok}
                      onChange={(e) => handleInputChange('sitioPurok', e.target.value)}
                      placeholder="e.g., Purok 5"
                    />
                  </div>
                  <div>
                    <Label htmlFor="barangay">Barangay *</Label>
                    <Input
                      id="barangay"
                      value={formData.barangay}
                      onChange={(e) => handleInputChange('barangay', e.target.value)}
                      placeholder="Enter barangay name"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="municipality">Municipality *</Label>
                    <Select value={formData.municipality} onValueChange={(value) => handleInputChange('municipality', value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select municipality" />
                      </SelectTrigger>
                      <SelectContent>
                        {municipalities.map(municipality => (
                          <SelectItem key={municipality} value={municipality}>
                            {municipality}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {/* latitude and longitude removed — not used by backend route */}
                </div>
              </div>

              {/* Farming Information */}
              <div>
                <h3 className="mb-4 text-green-800">Farming Information</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="landOwnership">Land Ownership *</Label>
                    <Select value={formData.landOwnership} onValueChange={(value) => handleInputChange('landOwnership', value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select ownership type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Owned">Owned</SelectItem>
                        <SelectItem value="Rented">Rented</SelectItem>
                        <SelectItem value="Shared">Shared</SelectItem>
                        <SelectItem value="Tenant">Tenant</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="landSize">Farm Size (Hectares) *</Label>
                    <Input
                      id="landSize"
                      type="number"
                      step="0.01"
                      value={formData.landSize}
                      onChange={(e) => handleInputChange('landSize', e.target.value)}
                      placeholder="e.g., 2.5"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="yearEstablished">Year Established</Label>
                    <Input
                      id="yearEstablished"
                      type="number"
                      value={farmData.yearEstablished}
                      onChange={(e) => handleFarmChange('yearEstablished', e.target.value)}
                      placeholder="e.g., 2015"
                    />
                  </div>
                  <div>
                    <Label htmlFor="actualTrees">Actual Number of Trees</Label>
                    <Input
                      id="actualTrees"
                      type="number"
                      value={farmData.actualTrees}
                      onChange={(e) => handleFarmChange('actualTrees', e.target.value)}
                      placeholder="e.g., 350"
                    />
                  </div>
                  <div>
                    <Label htmlFor="productiveTreesBefore">Productive Trees (Before)</Label>
                    <Input
                      id="productiveTreesBefore"
                      type="number"
                      value={farmData.productiveTreesBefore}
                      onChange={(e) => handleFarmChange('productiveTreesBefore', e.target.value)}
                      placeholder="e.g., 200"
                    />
                  </div>
                  <div>
                    <Label htmlFor="productiveTreesAfter">Productive Trees (After)</Label>
                    <Input
                      id="productiveTreesAfter"
                      type="number"
                      value={farmData.productiveTreesAfter}
                      onChange={(e) => handleFarmChange('productiveTreesAfter', e.target.value)}
                      placeholder="e.g., 280"
                    />
                  </div>
                  <div>
                    <Label htmlFor="sourceProvider">Source / Provider</Label>
                    <Input
                      id="sourceProvider"
                      value={farmData.sourceProvider}
                      onChange={(e) => handleFarmChange('sourceProvider', e.target.value)}
                      placeholder="e.g., Philippine Coconut Authority (PCA)"
                    />
                  </div>
                  <div>
                    <Label htmlFor="propagationTechnique">Propagation Technique</Label>
                    <Select value={farmData.propagationTechnique} onValueChange={(value) => handleFarmChange('propagationTechnique', value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select technique" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Budding">Budding</SelectItem>
                        <SelectItem value="Grafting">Grafting</SelectItem>
                        <SelectItem value="Seedling">Seedling</SelectItem>
                        <SelectItem value="Cutting">Cutting</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="clones">Clones</Label>
                    <Input
                      id="clones"
                      value={farmData.clones}
                      onChange={(e) => handleFarmChange('clones', e.target.value)}
                      placeholder="e.g., K1, K2, BR25"
                    />
                  </div>
                  <div>
                    <Label htmlFor="availabilityOfNursery">Availability of Nursery</Label>
                    <Input
                      id="availabilityOfNursery"
                      value={farmData.availabilityOfNursery}
                      onChange={(e) => handleFarmChange('availabilityOfNursery', e.target.value)}
                      placeholder="e.g., Yes, No, or custom text"
                    />
                  </div>
                  <div>
                    <Label htmlFor="productionCapacity">Production Capacity (kg/year) *</Label>
                    <Input
                      id="productionCapacity"
                      type="number"
                      value={formData.productionCapacity}
                      onChange={(e) => handleInputChange('productionCapacity', e.target.value)}
                      placeholder="e.g., 800"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Varieties Planted - Multi-add */}
              <div>
                <h3 className="mb-4 text-green-800">Varieties Planted</h3>
                <div className="space-y-3">
                  <div className="flex gap-2">
                    <Input
                      value={newVariety}
                      onChange={(e) => setNewVariety(e.target.value)}
                      placeholder="Enter variety (e.g., Forastero, Trinitario)"
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addVariety())}
                    />
                    <Button type="button" onClick={addVariety} variant="outline">
                      Add
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {cacaoVarieties.map((variety, index) => (
                      <Badge key={index} variant="secondary" className="flex items-center gap-1">
                        {variety}
                        <button
                          type="button"
                          onClick={() => removeVariety(variety)}
                          className="ml-1 hover:text-red-600"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>

                {/* Membership Information */}
                <div>
                  <h3 className="mb-4 text-green-800">Membership / Affiliation (optional)</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="affiliationName">Affiliation Name</Label>
                      <Input
                        id="affiliationName"
                        value={membershipData.affiliationName}
                        onChange={(e) => handleMembershipChange('affiliationName', e.target.value)}
                        placeholder="e.g., Women Cacao Growers Cooperative"
                      />
                    </div>
                    <div>
                      <Label htmlFor="yearsInMembership">Years in Membership</Label>
                      <Input
                        id="yearsInMembership"
                        type="number"
                        value={membershipData.yearsInMembership}
                        onChange={(e) => handleMembershipChange('yearsInMembership', e.target.value)}
                        placeholder="e.g., 3"
                      />
                    </div>
                    <div>
                      <Label htmlFor="position">Position / Designation</Label>
                      <Input
                        id="position"
                        value={membershipData.position}
                        onChange={(e) => handleMembershipChange('position', e.target.value)}
                        placeholder="e.g., Auditor, Treasurer"
                      />
                    </div>
                    <div>
                      <Label htmlFor="designation">Designation</Label>
                      <Input
                        id="designation"
                        value={membershipData.designation}
                        onChange={(e) => handleMembershipChange('designation', e.target.value)}
                        placeholder="e.g., Member"
                      />
                    </div>
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="active"
                        checked={membershipData.active}
                        onChange={(e) => handleMembershipChange('active', e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300"
                      />
                      <Label htmlFor="active">Active Member</Label>
                    </div>
                  </div>
                </div>

              {/* Training Received */}
              <div>
                <h3 className="mb-4 text-green-800">Training Received</h3>
                <div className="space-y-3">
                  <div className="flex gap-2">
                    <Input
                      value={newTraining}
                      onChange={(e) => setNewTraining(e.target.value)}
                      placeholder="Enter training program (e.g., Organic Farming)"
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTraining())}
                    />
                    <Button type="button" onClick={addTraining} variant="outline">
                      Add
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {trainingReceived.map((training, index) => (
                      <Badge key={index} variant="outline" className="flex items-center gap-1">
                        {training}
                        <button
                          type="button"
                          onClick={() => removeTraining(training)}
                          className="ml-1 hover:text-red-600"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>

                {/* Productivity Information */}
                <div>
                  <h3 className="mb-4 text-green-800">Productivity (optional)</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="productivityPodsTreeYear">Pods / tree / year</Label>
                      <Input
                        id="productivityPodsTreeYear"
                        type="number"
                        step="0.01"
                        value={productivityData.productivityPodsTreeYear}
                        onChange={(e) => handleProductivityChange('productivityPodsTreeYear', e.target.value)}
                        placeholder="e.g., 35.00"
                      />
                    </div>
                    <div>
                      <Label htmlFor="podsPerTreeHarvest">Pods / tree / harvest</Label>
                      <Input
                        id="podsPerTreeHarvest"
                        type="number"
                        step="0.01"
                        value={productivityData.podsPerTreeHarvest}
                        onChange={(e) => handleProductivityChange('podsPerTreeHarvest', e.target.value)}
                        placeholder="e.g., 22.00"
                      />
                    </div>
                    <div>
                      <Label htmlFor="weightOfBeansPerHarvestKg">Weight beans / harvest (kg)</Label>
                      <Input
                        id="weightOfBeansPerHarvestKg"
                        type="number"
                        step="0.01"
                        value={productivityData.weightOfBeansPerHarvestKg}
                        onChange={(e) => handleProductivityChange('weightOfBeansPerHarvestKg', e.target.value)}
                        placeholder="e.g., 40.00"
                      />
                    </div>
                    <div>
                      <Label htmlFor="weightBeansHectare">Weight beans / hectare (kg)</Label>
                      <Input
                        id="weightBeansHectare"
                        type="number"
                        step="0.01"
                        value={productivityData.weightBeansHectare}
                        onChange={(e) => handleProductivityChange('weightBeansHectare', e.target.value)}
                        placeholder="e.g., 950.00"
                      />
                    </div>
                  </div>
                </div>

                {/* Knowledge Data */}
                <div>
                  <h3 className="mb-4 text-green-800">Knowledge / Training Details (optional)</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="knowledgeOnCacaoFarming">Knowledge / Trainings (comma separated)</Label>
                      <Input
                        id="knowledgeOnCacaoFarming"
                        value={knowledgeData.knowledgeOnCacaoFarming}
                        onChange={(e) => handleKnowledgeChange('knowledgeOnCacaoFarming', e.target.value)}
                        placeholder="e.g., Pruning, Organic Farming"
                      />
                    </div>
                    <div>
                      <Label htmlFor="production">Primary Production / Notes</Label>
                      <Input
                        id="production"
                        value={knowledgeData.production}
                        onChange={(e) => handleKnowledgeChange('production', e.target.value)}
                        placeholder="optional notes"
                      />
                    </div>
                  </div>
                </div>

              {/* Submit Buttons */}
              <div className="flex justify-end gap-4 pt-6 border-t">
                <Button type="button" variant="outline" onClick={onClose}>
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  className="bg-amber-600 hover:bg-amber-700"
                  disabled={!isFormValid()}
                >
                  {isEditMode ? 'Update Farmer' : 'Add Farmer'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}